import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { TaskCard } from "../types";
import { formatDayMonth, formatTime } from "./cardStatus";
import { fullName, useRefreshFunnel } from "./hooks";
import { taskTiming } from "./todayLogic";

const badgeClass = {
  overdue: "bg-destructive/10 text-destructive",
  today: "bg-honey/20 text-warning",
  later: "bg-accent text-primary",
  none: "bg-muted text-muted-foreground",
};

/** A task in "Сьогодні" and the tasks page (docs/design/Main, TasksM) */
export const TaskRow = ({ task }: { task: TaskCard }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefreshFunnel();
  const { taskTypes } = useConfigurationContext();
  const now = new Date();
  const timing = taskTiming(task.due_date, now);
  const due = task.due_date ? new Date(task.due_date) : null;
  const title =
    task.text ||
    taskTypes.find((type) => type.value === task.type)?.label ||
    "";
  const badge =
    timing === "overdue"
      ? due &&
        now.getTime() - due.getTime() < 2 * 24 * 3600_000 &&
        due.getDate() !== now.getDate()
        ? translate("crm.today.yesterday")
        : translate("crm.today.overdue_badge")
      : timing === "today" && due
        ? formatTime(due)
        : timing === "later" && due
          ? formatDayMonth(due)
          : null;

  const done = async () => {
    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: { done_date: new Date().toISOString() },
        previousData: task,
      });
      notify("crm.tasks_page.done");
      refresh();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  const body = (
    <>
      <div className="font-bold leading-tight">{title}</div>
      <div className="truncate text-sm text-muted-foreground">
        {fullName(task)}
      </div>
    </>
  );

  return (
    <div className="flex items-center gap-3 rounded-2xl border bg-card p-4">
      <Checkbox className="size-6" aria-label={title} onCheckedChange={done} />
      {task.deal_id ? (
        <Link
          to={`/funnel/${task.deal_id}`}
          className="min-w-0 flex-1 text-foreground no-underline"
        >
          {body}
        </Link>
      ) : (
        <div className="min-w-0 flex-1">{body}</div>
      )}
      {badge ? (
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-xs font-bold",
            badgeClass[timing],
          )}
        >
          {badge}
        </span>
      ) : null}
    </div>
  );
};
